/**
 * Detecting English talks: access level, language tag, or title heuristic.
 *
 * No imports.
 */

// This list and `src/talklang.ts`'s (the Worker's copy) are reconciled to be
// identical, word for word — `test/talklang-lists-agree.test.ts` reads both
// files and fails the moment they stop being. Neither file may import the
// other (both are import-free by rule, and they live in different
// projects), so the two literals are the shared contract instead.
//
// "in" and "a"/"an" are deliberately absent from the English list: both are
// also German words ("in" identically; "a"/"an" only avoided as a class of
// short function words with a German near-twin), and `src/german.ts`'s own
// comment already documents dropping "in" and "a" from its English set for
// exactly this reason — a bare "in" once made a German sentence about
// Brandenburg read as English there.

/** English stopwords. */
const EN_STOP = new Set([
  'the', 'of', 'and', 'in', 'for', 'with', 'at', 'to', 'from',
  'is', 'are', 'were', 'that', 'this', 'his', 'her', 'their', 'its',
  'which', 'who', 'have', 'has', 'but', 'been', 'into', 'on', 'by', 'as',
  'not', 'you', 'your', 'we', 'our', 'all', 'about', 'between', 'after',
  'before', 'under', 'over',
])

/** German stopwords. */
const DE_STOP = new Set([
  'der', 'die', 'das', 'und', 'für', 'mit', 'im', 'am', 'von', 'zum',
  'ein', 'eine', 'einer', 'eines', 'einem', 'einen', 'ist', 'sind', 'auf',
  'bei', 'als', 'dass', 'diese', 'dieser', 'dieses', 'wird', 'werden',
  'wir', 'ihr', 'ihre', 'unser', 'alle', 'über', 'unter', 'zwischen',
  'durch', 'während', 'vor', 'nach', 'ohne',
])

interface EventForLang {
  type: string
  english_access?: string
  event_lang?: string | null
  title?: string
  /** `'lead'` on a row promoted from a newsletter (blind spots N3). */
  source_recipe?: string | null
}

/**
 * The types this file's English rule governs — talks and tours read the
 * same three signals below. The one set both this file and `entries.ts`'s
 * `talkLanguageAllowed` read, so which types are gated is decided once,
 * here, and not as two separately-maintained literals that could drift
 * (which is how `isEnglishTalk` alone excluding `tour` went unnoticed: the
 * caller had already widened its own copy of this list to include tours,
 * and never saw the inner one still narrower).
 */
export const TALK_LIKE_TYPES = new Set(['talk', 'tour'])

/**
 * True if title looks predominantly English based on stopword frequency.
 *
 * Lowercase, split on non-letters, count hits against English and German
 * stopword lists. English wins when it has ≥2 hits and more than German;
 * a title with no hits is not English.
 */
function titleLooksEnglish(title: string | undefined): boolean {
  if (!title) return false

  const words = title
    .toLowerCase()
    .split(/[^a-zäöüß]+/)
    .filter(w => w.length > 0)

  let enHits = 0, deHits = 0
  for (const word of words) {
    if (EN_STOP.has(word)) enHits++
    if (DE_STOP.has(word)) deHits++
  }

  return enHits >= 2 && enHits > deHits
}

/**
 * True if the event is a talk or tour with English access.
 *
 * A talk or tour is English when: english_access is 'native', or event_lang
 * starts with 'en', or the title looks English by stopword analysis.
 * Anything else is never English here, whatever its signals say — this
 * function only answers the language question for the types
 * `TALK_LIKE_TYPES` names.
 */
export function isEnglishTalk(e: EventForLang): boolean {
  if (!TALK_LIKE_TYPES.has(e.type)) return false
  if (e.source_recipe === 'lead') return leadTalkIsEnglish(e)

  if (e.english_access === 'native') return true
  if (e.event_lang && e.event_lang.startsWith('en')) return true
  if (titleLooksEnglish(e.title)) return true

  return false
}

/**
 * A talk promoted from a newsletter (blind spots K5, A2). The newsletter's
 * own language is not the event's — a German mail can announce an English
 * lecture, and an English digest can list a German reading — so what N3
 * wrote from the lead's `language` decides first, and a decided "German" is
 * final: `english_access: 'none'` or a `de` tag hides the talk, where a
 * harvested row's English-looking title would still let it through. Only
 * when nothing was decided does the title speak, and only the title: the
 * row's summary is a sentence copied from the newsletter, in the
 * newsletter's language, and says nothing about the evening's.
 */
function leadTalkIsEnglish(e: EventForLang): boolean {
  if (e.english_access === 'native') return true
  if (e.english_access === 'none') return false
  if (e.event_lang) return e.event_lang.startsWith('en')
  return titleLooksEnglish(e.title)
}

export { titleLooksEnglish }
